import { useState } from 'react';
import type { PortalMenu } from '@me-get/shared';
import { validateUrl } from '@me-get/shared';

export type MenuContentType = 'html_embed' | 'external_url' | 'service_link';

export interface MenuFormData {
  label: string;
  targetUrl: string;
  displayOrder: number;
  icon: string;
  contentType: MenuContentType;
  htmlContent: string;
}

interface MenuEditorProps {
  menus: PortalMenu[];
  companyId: string | null;
  isShared: boolean;
  onSave: (data: MenuFormData, id?: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  loading?: boolean;
}

const emptyForm: MenuFormData = {
  label: '',
  targetUrl: '',
  displayOrder: 1,
  icon: '',
  contentType: 'external_url',
  htmlContent: '',
};

const menuContentTypeLabels: Record<MenuContentType, string> = {
  html_embed: 'HTML Embed',
  external_url: 'External URL',
  service_link: 'Service Link',
};

export function MenuEditor({ menus, companyId, isShared, onSave, onDelete, loading }: MenuEditorProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<MenuFormData>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  function validate(data: MenuFormData): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!data.label.trim()) {
      errs.label = 'Label is required';
    }
    if (!data.targetUrl.trim()) {
      errs.targetUrl = 'Target URL is required';
    } else if (data.contentType === 'external_url') {
      // Only validate URL format for external_url; service_link can use relative paths
      const urlResult = validateUrl(data.targetUrl);
      if (!urlResult.valid) {
        errs.targetUrl = urlResult.errors[0];
      }
    }
    if (!Number.isInteger(data.displayOrder) || data.displayOrder < 1) {
      errs.displayOrder = 'Display order must be a positive integer';
    }
    return errs;
  }

  function handleEdit(menu: PortalMenu) {
    setEditingId(menu.id);
    setFormData({
      label: menu.label,
      targetUrl: menu.targetUrl,
      displayOrder: menu.displayOrder,
      icon: menu.icon ?? '',
      contentType: (menu.contentType as MenuContentType) ?? 'external_url',
      htmlContent: menu.htmlContent ?? '',
    });
    setErrors({});
    setShowForm(true);
  }

  function handleNew() {
    setEditingId(null);
    const nextOrder = menus.length > 0 ? Math.max(...menus.map((m) => m.displayOrder)) + 1 : 1;
    setFormData({ ...emptyForm, displayOrder: nextOrder });
    setErrors({});
    setShowForm(true);
  }

  function handleCancel() {
    setShowForm(false);
    setEditingId(null);
    setFormData(emptyForm);
    setErrors({});
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSaving(true);
    try {
      await onSave(formData, editingId ?? undefined);
      handleCancel();
    } finally {
      setSaving(false);
    }
  }

  function handleFieldChange(field: keyof MenuFormData, value: string | number) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  const sortedMenus = [...menus].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="menu-editor">
      <div className="menu-editor__header">
        <h3>{isShared ? 'Shared Menus' : 'Company Menus'}</h3>
        <button className="btn btn-primary" onClick={handleNew} disabled={loading || saving}>
          + Add Menu
        </button>
      </div>

      {showForm && (
        <form className="menu-editor__form" onSubmit={handleSubmit} aria-label="Menu form">
          <div className="form-group">
            <label htmlFor="menu-label">Label *</label>
            <input
              id="menu-label"
              type="text"
              value={formData.label}
              onChange={(e) => handleFieldChange('label', e.target.value)}
              placeholder="Menu label"
              aria-invalid={!!errors.label}
              aria-describedby={errors.label ? 'menu-label-error' : undefined}
            />
            {errors.label && <span id="menu-label-error" className="form-error">{errors.label}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="menu-target-url">Target URL *</label>
            <input
              id="menu-target-url"
              type="text"
              value={formData.targetUrl}
              onChange={(e) => handleFieldChange('targetUrl', e.target.value)}
              placeholder="https://example.com"
              aria-invalid={!!errors.targetUrl}
              aria-describedby={errors.targetUrl ? 'menu-url-error' : undefined}
            />
            {errors.targetUrl && <span id="menu-url-error" className="form-error">{errors.targetUrl}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="menu-display-order">Display Order *</label>
              <input
                id="menu-display-order"
                type="number"
                min="1"
                value={formData.displayOrder}
                onChange={(e) => handleFieldChange('displayOrder', parseInt(e.target.value, 10) || 0)}
                aria-invalid={!!errors.displayOrder}
                aria-describedby={errors.displayOrder ? 'menu-order-error' : undefined}
              />
              {errors.displayOrder && <span id="menu-order-error" className="form-error">{errors.displayOrder}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="menu-icon">Icon (optional)</label>
              <input
                id="menu-icon"
                type="text"
                value={formData.icon}
                onChange={(e) => handleFieldChange('icon', e.target.value)}
                placeholder="Icon name or URL"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="menu-content-type">Content Type</label>
            <select
              id="menu-content-type"
              value={formData.contentType}
              onChange={(e) => handleFieldChange('contentType', e.target.value as MenuContentType)}
            >
              {(Object.keys(menuContentTypeLabels) as MenuContentType[]).map((type) => (
                <option key={type} value={type}>
                  {menuContentTypeLabels[type]}
                </option>
              ))}
            </select>
          </div>

          {formData.contentType === 'html_embed' && (
            <div className="form-group">
              <label htmlFor="menu-html-content">HTML Content</label>
              <textarea
                id="menu-html-content"
                value={formData.htmlContent}
                onChange={(e) => handleFieldChange('htmlContent', e.target.value)}
                placeholder="Enter HTML content for embed"
                rows={4}
              />
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update Menu' : 'Create Menu'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="loading-text">Loading menus...</p>
      ) : sortedMenus.length === 0 ? (
        <p className="empty-text">No menus configured yet.</p>
      ) : (
        <table className="data-table" aria-label="Menus list">
          <thead>
            <tr>
              <th>Order</th>
              <th>Label</th>
              <th>Target URL</th>
              <th>Icon</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedMenus.map((menu) => (
              <tr key={menu.id}>
                <td>{menu.displayOrder}</td>
                <td>{menu.label}</td>
                <td className="url-cell">{menu.targetUrl}</td>
                <td>{menu.icon ?? '—'}</td>
                <td className="actions-cell">
                  <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(menu)}>
                    Edit
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => onDelete(menu.id)}
                    aria-label={`Delete menu ${menu.label}`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
